import { Directive, ElementRef, HostListener, Input, Renderer2 } from '@angular/core';
/**
 * Directive pour afficher une tooltip riche capable d'afficher de l'HTML.
 * Exemple :
 * <button [hubToolTip]="tooltipContent" [tooltipPosition]="'right'">Survolez-moi</button>
 * tooltipContent = '<h1>Exemple de tooltip</h1><strong>Ceci</strong> est un exemple de tooltip en HTML';
 */
@Directive({
    selector: '[hubToolTip]',
    standalone: true,
})
export class RichToolTipDirective {
    /**
     * Contenu à afficher dans la tooltip.
     */
    @Input('hubToolTip') tooltipContent: string = '';

    /**
     * Position par défaut de la tooltip.
     */
    @Input() tooltipPosition: 'top' | 'bottom' | 'left' | 'right' = 'top';

    /**
     * Element Html contenant la tooltip.
     */
    private tooltipElement: HTMLElement | null = null;

    /**
     * Valeur indiquant si la tooltip est affichée.
     */
    private tooltipVisible: boolean = false;

    /**
     * Une reference à l'instruction setTimeout utilisé pour fermer la tooltip.
     */
    private hideTimeoutRef: any = null;

    /**
     * Constructeur.
     * @param el : Reference à l'element affichant la tooltip.
     * @param renderer : Classe utilisé pour le rendu.
     */
    constructor(
        private el: ElementRef,
        private renderer: Renderer2
    ) {}

    /**
     * Abonnement aux evenements de souris 'mouseenter'
     */
    @HostListener('mouseenter') onMouseEnter() {
        this.createTooltip();
        this.tooltipVisible = true;
    }

    /**
     * Abonnement aux evenements de souris 'mouseleave'
     */
    @HostListener('mouseleave') onMouseLeave() {
        this.hideTooltip();
    }

    /**
     * Crée la tooltip.
     */
    private createTooltip() {
        if (!this.tooltipElement) {
            this.tooltipElement = this.renderer.createElement('div');
            this.renderer.addClass(this.tooltipElement, 'rich-tooltip');
            this.renderer.setProperty(this.tooltipElement, 'innerHTML', this.tooltipContent);

            this.renderer.listen(this.tooltipElement, 'mouseenter', () => {
                this.keepTooltipVisible(); // Garder le tooltip visible
            });

            this.renderer.listen(this.tooltipElement, 'mouseleave', () => {
                this.hideTooltip();
            });

            this.renderer.appendChild(document.body, this.tooltipElement);

            /* Gestion de l'emplacement où est affiché la tooltip */

            const { left, top, width, height } = this.el.nativeElement.getBoundingClientRect();
            const tooltipHeight = this.tooltipElement.offsetHeight;
            const tooltipWidth = this.tooltipElement.offsetWidth;

            let tooltipTop: number;
            let tooltipLeft: number;

            // Positionnement selon la position spécifiée
            switch (this.tooltipPosition) {
                case 'top':
                    tooltipTop = top - tooltipHeight - 5; // Positionner au-dessus
                    tooltipLeft = left + width / 2 - tooltipWidth / 2;
                    break;
                case 'bottom':
                    tooltipTop = top + height + 5; // Positionner en dessous
                    tooltipLeft = left + width / 2 - tooltipWidth / 2;
                    break;
                case 'left':
                    tooltipTop = top + height / 2 - tooltipHeight / 2; // Centrer verticalement
                    tooltipLeft = left - tooltipWidth - 5; // Positionner à gauche
                    break;
                case 'right':
                    tooltipTop = top + height / 2 - tooltipHeight / 2; // Centrer verticalement
                    tooltipLeft = left + width + 5; // Positionner à droite
                    break;
                default:
                    tooltipTop = top - tooltipHeight - 5; // Fallback par défaut
                    tooltipLeft = left + width / 2 - tooltipWidth / 2;
            }

            // Vérifier les débordements
            const windowHeight = window.innerHeight;
            const windowWidth = window.innerWidth;

            // Ajustements pour éviter les débordements
            if (tooltipTop < 0) {
                tooltipTop = top + height + 5; // Revenir en dessous si débordement
            }
            if (tooltipTop + tooltipHeight > windowHeight) {
                tooltipTop = top - tooltipHeight - 5; // Revenir au-dessus si débordement
            }
            if (tooltipLeft < 0) {
                tooltipLeft = 5; // Assurer un espacement
            }
            if (tooltipLeft + tooltipWidth > windowWidth) {
                tooltipLeft = windowWidth - tooltipWidth - 5; // Ajuster à gauche
            }

            this.renderer.setStyle(this.tooltipElement, 'left', `${tooltipLeft}px`);
            this.renderer.setStyle(this.tooltipElement, 'top', `${tooltipTop}px`);

            // Ajouter la classe visible pour l'animation
            setTimeout(() => {
                this.renderer.addClass(this.tooltipElement, 'visible');
            }, 0); // Utiliser setTimeout pour s'assurer que la classe est ajoutée après l'ajout au DOM
        }
    }

    /**
     * Garde la tooltip visible.
     */
    private keepTooltipVisible() {
        if (this.hideTimeoutRef) {
            this.renderer.removeClass(this.tooltipElement, 'hidden');
            this.renderer.addClass(this.tooltipElement, 'visible');
            // Annule la fermeture de la tooltip.
            clearTimeout(this.hideTimeoutRef);
        }
    }

    /**
     * Masque la tooltip.
     */
    private hideTooltip() {
        if (this.tooltipElement && this.tooltipVisible) {
            // Remplace la classe visible par hidden pour l'animation de disparition
            this.renderer.removeClass(this.tooltipElement, 'visible');
            this.renderer.addClass(this.tooltipElement, 'hidden');
            // Attendre la fin de l'animation avant de supprimer l'élément
            this.hideTimeoutRef = setTimeout(() => {
                this.renderer.removeChild(document.body, this.tooltipElement);
                this.tooltipElement = null;
                this.tooltipVisible = false; // Réinitialiser l'état de visibilité
            }, 300); // Correspond à la durée de la transition
        }
    }
}
